import { EventEmitter, InjectionToken } from '@angular/core';
import { AccountDetailsResponse, TradingAccount } from '../../../../domain/account.domain';
import { AccountsContainer } from '../../../../features/accounts/services/accounts-container';
import { OptionQuoteParams, OptionType, OrderEntryConfiguration } from '../../../../domain/trading.domain';
import { Quote } from '../../../../domain/quote.domain';
import { MarketType } from '../../../../domain/market.domain';
import { SecurityInfo, SymbolType } from '../../../../domain/symbol.domain';
import { OrderSecurity } from '../../../../domain/security.domain';
import {
  EquityOrderAction,
  InsiderType,
  MutualFundDividendType,
  MutualFundOrderAction,
  MutualFundQuantityType,
  OptionOrderAction,
  OptionPriceType,
  OrderActionParam,
  OrderDateType,
  PricePlan,
  PriceType,
  Strategy
} from '../../domain/order.enum';
import {
  EquityOptionsCommonModel,
  Order,
  OrderCapture
} from '../../domain/order.domain';
import {
  GicList,
  GicResultsItem,
  GicTradeParams,
  GicType,
  PaymentFrequency
} from '../../domain/gic.domain';
import { OptionQuoteData } from '../../domain/order.quote.domain';

export const ORDER_ENTRY_USER_CONFIGURATION = new InjectionToken<OrderEntryConfiguration>('orderEntryUserConfiguration');

export interface OrderFormParameters {
  orderAccounts: AccountsContainer<TradingAccount>;
  orderStateParameters: OrderStateParameters;
  orderDetails: OrderCapture;
  fractionalEligibility?: FractionalEligibility;
}

export interface OrderDetailsInformation {
  orderDetails: Order;
  accountDetails: AccountDetailsResponse;
  orderType?: OrderSecurity;
  orderStateParameters: OrderStateParameters;
  fractionalEligibility?: FractionalEligibility;
}

export interface OrderDetailsParameters {
  orderKey: string;
  requestFills: boolean;
}

export interface OptionSelection {
  nonStandard: boolean;
  underlyingSymbol: string;
  expiryDate: Date;
  strikePrice: number;
  optionType: OptionType;
  priceType: OptionPriceType;
  value: number;
}

export interface OrderStateParameters {
  readonly actionType: string;
  readonly orderKey: string;
  readonly operation: string;
  readonly action: string | OrderActionParam;
  readonly symbol: string;
  readonly market: MarketType;
  readonly accountId: string;
  readonly quantity: number;
  readonly optionNonStandard: boolean;
  readonly securityType: string;
  price: PriceType; // TODO: fix and make readonly
  readonly orderView: string;
  readonly reverse: string;
  readonly simplifiedOrder: boolean;
  readonly orderType?: OrderSecurity;
}

export interface GicOrderStateParameters extends OrderStateParameters {
  readonly gicType?: string;
  readonly gicTerm?: string;
  readonly gicFrequency?: string;
  readonly gicRate?: number;
}

export type OptionSymbolUrlParam = Pick<OptionSelection, 'underlyingSymbol' | 'expiryDate' | 'strikePrice' | 'optionType'>;

export declare type OrderStepType = 'edit' | 'review' | 'complete' | 'thankYou' | 'done';
export declare type StrikeNumberType = '6' | '8' | '12' | '16' | 'ALL';
export declare type Month = 'JAN' | 'FEB' | 'MAR' | 'APR' | 'MAY' | 'JUN' | 'JUL' | 'AUG' | 'SEP' | 'OCT' | 'NOV' | 'DEC';

export enum OrderStep {
  edit = 'edit',
  review = 'review',
  complete = 'complete',
  thankYou = 'thankYou',
  done = 'done'
}

export interface OutputStep {
  stepChange: EventEmitter<OrderStepType>;
}

// TODO - review. The following is a copy from src/webbroker/modules/trading/domain/order.domain.ts with additional fields
export interface GicFormModel {
  gicList: GicList;
  selectedType: GicType;
  selectedTerm: number;
  selectedFrequency: PaymentFrequency;
  selectedGic: GicResultsItem;
  currentDate: Date;
  maturityDate?: Date;
  orderAmount: number;
  isGicMarketClosed: boolean;
  gicResults: Array<GicResultsItem>;
  rateSheetParams?: GicTradeParams;
}

export type OrderEntryFormModel =
  EquityFormModel |
  OptionFormModel |
  MutualFundFormModel |
  GicFormModel |
  MultilegFormModel |
  MultilegItemFormModel |
  Array<EquityFormModel| OptionFormModel> |
  StrategyFormModel;

export type OrderEntryChangeFormModel =
  EquityFormModel |
  OptionFormModel |
  MultilegFormModel |
  Array<EquityFormModel| OptionFormModel> |
  StrategyFormModel;

export interface StrategyFormModel {
  items: Array<EquityFormModel | OptionFormModel | MultilegItemFormModel>;
  strategyType: Strategy;
}

export interface OTAFormModel extends StrategyFormModel {
  firstSecurityType: SymbolType;
  secondSecurityType: SymbolType;
}

export interface EquityFormModel extends EquityOptionsCommonModel {
  orderAction: EquityOrderAction;
  shareholderStatus: InsiderType;
  simplifiedOrder?: boolean;
  reverse?: boolean;
  orderQuantityLimit?: number;
  orderAmount?: number;
  focusedField?: CalculatorField;
  fractionalType?: CalculatorFractionalTypes;
  fractionalEligibility?: FractionalEligibility;
  isCopyOrder?: boolean;
}

export interface OptionFormModel extends EquityOptionsCommonModel {
  orderAction: OptionOrderAction;
  optionDetails?: Array<OptionQuoteData>;
  optionQuoteParams?: OptionQuoteParams;
  nonStandard: boolean;
}

export interface MutualFundFormModel {
  selectedSymbol?: SecurityInfo;
  orderAction?: MutualFundOrderAction;
  orderQuantityType?: MutualFundQuantityType;
  quantityTypeAmount?: number;
  quantityTypeUnits?: number;
  dividendOption?: MutualFundDividendType;
  includeCommissionIndicator?: boolean;
  switchTo?: { type: string; symbol: string };
  toSelectedSymbol?: SecurityInfo;
}

export interface MultilegFormModel extends StrategyFormModel {
  strategyType: Strategy;
  selectedType: SymbolType.EQUITY | SymbolType.OPTION;
  selectedSymbol: SecurityInfo;
  firstLeg: MultilegItemFormModel;
  secondLeg: MultilegItemFormModel;
  limitPrice: number;
  pricePlan: PricePlan;
  goodTill: OrderDateType.DAY | OrderDateType.DATE | OrderDateType.CANCEL;
  goodTillDate: Date;
}

// TODO Some of these properties are just supporting data for the screen and shouldnt be on the model
export interface MultilegItemFormModel {
  orderAction: EquityOrderAction | OptionOrderAction;
  orderQuantity: number;
  optionDetails?: Array<OptionQuoteData>;
  optionQuoteParams?: OptionQuoteParams;
  quote?: Quote;
  optionPrepopulateValues?: OptionSymbolUrlParam;
  underlyingType?: SymbolType;
  underlyingTicker?: string;
  id?: string;
}

export interface FractionalEligibility {
  symbol: string;
  fractionalBuy: boolean;
  fractionalSell: boolean;
}

export type CalculatorField = 'quantity' | 'amount' | undefined;

export interface CalculatorFieldChangeEvent {
  disableAdvancedOptions: boolean;
  focusedField: CalculatorField;
  orderQuantity: number;
  orderAmount: number;
  fractionalType: CalculatorFractionalTypes;
}

export enum CalculatorFractionalTypes {
  NOT_ELIGIBLE = 'non-eligible',
  FRACTIONAL = 'fractional',
  NOTIONAL = 'notional'
}

export interface FractionalCoachMarkStatus {
  fractionalCoachMarkDisplayed: boolean;
  nonFractionalCoachMarkDisplayed: boolean;
}

